import { z } from "zod";

const nullableString = (maxLength: number) => z.string().trim().min(1).max(maxLength).nullable();
const moneySchema = z.number().finite().min(0).max(9_999_999_999.99).nullable();
const currencyCodeSchema = z.string().regex(/^[A-Z]{3}$/);

export const specialistRateModels = [
  "hourly",
  "monthly",
  "hourly_or_monthly",
  "negotiable",
] as const;
export const specialistWorkFormats = ["remote", "hybrid", "onsite", "flexible"] as const;
export const specialistEmploymentTypes = [
  "full_time",
  "part_time",
  "contract",
  "freelance",
  "project_based",
] as const;
export const specialistDesiredWorkloads = ["full_time", "part_time", "flexible"] as const;

export const specialistRateModelSchema = z.enum(specialistRateModels);
export const specialistWorkFormatSchema = z.enum(specialistWorkFormats);
export const specialistEmploymentTypeSchema = z.enum(specialistEmploymentTypes);
export const specialistDesiredWorkloadSchema = z.enum(specialistDesiredWorkloads);

export const specialistWorkPreferencesSchema = z.object({
  id: z.string().uuid(),
  specialistProfileId: z.string().uuid(),
  rateModel: specialistRateModelSchema,
  currencyCode: currencyCodeSchema,
  hourlyRateMin: z.number().nullable(),
  hourlyRateMax: z.number().nullable(),
  monthlyRateMin: z.number().nullable(),
  monthlyRateMax: z.number().nullable(),
  workFormat: specialistWorkFormatSchema,
  employmentType: specialistEmploymentTypeSchema,
  desiredWorkload: specialistDesiredWorkloadSchema,
  notes: z.string().nullable(),
  createdByUserId: z.string().uuid(),
  updatedByUserId: z.string().uuid().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

function validateRanges(
  value: {
    hourlyRateMin?: number | null | undefined;
    hourlyRateMax?: number | null | undefined;
    monthlyRateMin?: number | null | undefined;
    monthlyRateMax?: number | null | undefined;
  },
  context: z.RefinementCtx,
) {
  if (
    value.hourlyRateMin !== undefined &&
    value.hourlyRateMax !== undefined &&
    value.hourlyRateMin !== null &&
    value.hourlyRateMax !== null &&
    value.hourlyRateMax < value.hourlyRateMin
  ) {
    context.addIssue({
      code: "custom",
      path: ["hourlyRateMax"],
      message: "hourlyRateMax cannot be lower than hourlyRateMin",
    });
  }

  if (
    value.monthlyRateMin !== undefined &&
    value.monthlyRateMax !== undefined &&
    value.monthlyRateMin !== null &&
    value.monthlyRateMax !== null &&
    value.monthlyRateMax < value.monthlyRateMin
  ) {
    context.addIssue({
      code: "custom",
      path: ["monthlyRateMax"],
      message: "monthlyRateMax cannot be lower than monthlyRateMin",
    });
  }
}

export const specialistWorkPreferencesPutRequestSchema = z
  .object({
    rateModel: specialistRateModelSchema.default("negotiable"),
    currencyCode: currencyCodeSchema.default("RUB"),
    hourlyRateMin: moneySchema.default(null),
    hourlyRateMax: moneySchema.default(null),
    monthlyRateMin: moneySchema.default(null),
    monthlyRateMax: moneySchema.default(null),
    workFormat: specialistWorkFormatSchema.default("flexible"),
    employmentType: specialistEmploymentTypeSchema.default("contract"),
    desiredWorkload: specialistDesiredWorkloadSchema.default("flexible"),
    notes: nullableString(4_000).default(null),
  })
  .strict()
  .superRefine(validateRanges);

export const specialistWorkPreferencesPatchRequestSchema = z
  .object({
    rateModel: specialistRateModelSchema.optional(),
    currencyCode: currencyCodeSchema.optional(),
    hourlyRateMin: moneySchema.optional(),
    hourlyRateMax: moneySchema.optional(),
    monthlyRateMin: moneySchema.optional(),
    monthlyRateMax: moneySchema.optional(),
    workFormat: specialistWorkFormatSchema.optional(),
    employmentType: specialistEmploymentTypeSchema.optional(),
    desiredWorkload: specialistDesiredWorkloadSchema.optional(),
    notes: nullableString(4_000).optional(),
  })
  .strict()
  .superRefine(validateRanges);

export const specialistWorkPreferencesResponseSchema = z.object({
  item: specialistWorkPreferencesSchema.nullable(),
});

export type SpecialistRateModel = z.infer<typeof specialistRateModelSchema>;
export type SpecialistWorkFormat = z.infer<typeof specialistWorkFormatSchema>;
export type SpecialistEmploymentType = z.infer<typeof specialistEmploymentTypeSchema>;
export type SpecialistDesiredWorkload = z.infer<typeof specialistDesiredWorkloadSchema>;
export type SpecialistWorkPreferences = z.infer<typeof specialistWorkPreferencesSchema>;
export type SpecialistWorkPreferencesPutRequest = z.infer<
  typeof specialistWorkPreferencesPutRequestSchema
>;
export type SpecialistWorkPreferencesPatchRequest = z.infer<
  typeof specialistWorkPreferencesPatchRequestSchema
>;
export type SpecialistWorkPreferencesResponse = z.infer<
  typeof specialistWorkPreferencesResponseSchema
>;
